import React, { useRef, useState } from 'react';
import Typography from '@mui/material/Typography';
import ActionLogsTable from '../tables/ActionLogsTable';
import ActionLogsPageFilter from '../filters/ActionLogsPageFilter/ActionLogsPageFilter';
import { ApiInstance } from '../../api/api';
import { ActionLogsDataType, FilterValues } from '../../types/actionLogsTypes';
import pageStyles from './pageStyles';

const ActionLogsPage: React.FC = () => {
  const [loadingState, setLoadingState] = useState(false);
  const [apiErrorFlag, setApiErrorFlag] = useState(false);
  const [allActionLogs, setAllActionLogs] = React.useState<ActionLogsDataType[]>([]);

  const pageSize = 5000;
  const batchSize = 5;
  const latestRequestId = useRef(0);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchAllActionLogs = async (appliedFilters: FilterValues) => {
    // Cancel any in-flight fetch for a stale filter to avoid wasted requests.
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const requestId = ++latestRequestId.current;

    setLoadingState(true);
    setAllActionLogs([]);
    setApiErrorFlag(false);

    try {
      const firstPageResult = await ApiInstance.getActionLogs(
        {
          page: 1,
          limit: pageSize,
          ...appliedFilters,
        },
        controller.signal
      );

      const allFetchedLogs: ActionLogsDataType[] = [...firstPageResult.data];
      const totalPages: number = firstPageResult.pagination.total_pages;

      let currentPage = 2;

      while (currentPage <= totalPages && requestId === latestRequestId.current) {
        const batchRequests = [];
        for (let i = 0; i < batchSize && currentPage <= totalPages; i++, currentPage++) {
          batchRequests.push(
            ApiInstance.getActionLogs(
              {
                page: currentPage,
                limit: pageSize,
                ...appliedFilters,
              },
              controller.signal
            )
          );
        }

        const responses = await Promise.all(batchRequests);
        responses.forEach((res) => allFetchedLogs.push(...res.data));
      }

      if (requestId === latestRequestId.current) {
        setAllActionLogs(allFetchedLogs);
      }
    } catch {
      // Aborted (superseded) requests are expected; the requestId guard
      // below skips them.
      if (requestId === latestRequestId.current) {
        setApiErrorFlag(true);
        setAllActionLogs([]);
      }
    } finally {
      if (requestId === latestRequestId.current) {
        setLoadingState(false);
      }
    }
  };

  const handleApply = (appliedFilters: FilterValues) => {
    fetchAllActionLogs(appliedFilters);
  };

  return (
    <>
      <Typography sx={pageStyles.label} component="h1" variant="h4" align="center">
        User action logs
      </Typography>
      <ActionLogsPageFilter onApply={handleApply} />
      <ActionLogsTable
        allActionLogs={allActionLogs}
        isLoading={loadingState}
        apiErrorFlag={apiErrorFlag}
      />
    </>
  );
};

export default ActionLogsPage;
